import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GeralGoApi } from '@gestor-web/@core/api/go';
import { UsuarioPerfil } from '@gestor-web/@core/api/ords/model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { GeralService, NotificationService } from '@gestor-web/app/shared/services';
import Keycloak from 'keycloak-js';
import { Observable, combineLatest, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, timeout } from 'rxjs/operators';
import { UserProfileActions } from '../actions';
import { OperadorasSelectors, RouterSelectors, UserProfileSelectors } from '../selectors';
import { vinculosDoWorkspace } from '../../auth/abrangencia';
import { AuthService } from '@gestor-web/@core/auth/service';
import { divergenciasPerfilToken } from '../../auth/divergencias-perfil-token';

/**
 * Quanto o front espera o perfil e a lista de funcionalidades antes de dar o serviço por
 * inacessível. Os guards de perfil e de workspace seguram a navegação até eles chegarem, então um
 * backend que não responde deixava a tela em branco sem aviso.
 */
export const CARREGAMENTO_TIMEOUT_MS = 30_000;

@Injectable()
export class UserProfileEffects {
  fullProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.getFullProfile),
      switchMap(() => [UserProfileActions.getIdssProfile(), UserProfileActions.getKeycloakProfile()]),
    ),
  );

  idssProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.getIdssProfile),
      switchMap(() =>
        this.geralGoApi.fetchPerfilCorrente().pipe(
          timeout(CARREGAMENTO_TIMEOUT_MS),
          map(profile => ({ profile, operadoras: vinculosDoWorkspace(profile) })),
          mergeMap(fullProfile => combineLatest([of(fullProfile), this.geralGoApi.updateUltimoAcesso()])),
          map(([{ profile, operadoras }]) => UserProfileActions.getIdssProfileSuccess({ idss: profile, operadoras })),
          // Sem status (timeout, rede), a falha do perfil leva a `/offline`.
          catchError((error: Partial<HttpErrorResponse>) =>
            of(
              UserProfileActions.getIdssProfileFailure({
                status: error.status ?? 0,
                message: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  funcionalidades$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.getFuncionalidades),
      switchMap(({ regans }) =>
        this.geralGoApi.fetchUsuarioFuncionalidades(regans).pipe(
          timeout(CARREGAMENTO_TIMEOUT_MS),
          map(resp => UserProfileActions.getFuncionalidadesSuccess({ funcionalidades: resp?.items ?? [] })),
          // O `TimeoutError` não tem status: conta como sem resposta, igual a falha de rede.
          catchError((error: Partial<HttpErrorResponse>) =>
            of(
              UserProfileActions.getFuncionalidadesFailure({
                status: error.status ?? 0,
                message: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Carrega a lista de funcionalidades assim que o perfil chega, junto com o perfil e as
   * operadoras, para todo usuário: sem workspace, vêm só as concessões do papel; a troca de
   * workspace recarrega com a operadora (`OperadorasEffects.carregarFuncionalidadesWorkspace$`).
   * Sem isto, quem abre uma tela fora do workspace (Dados do Setor) nunca teria a lista, e o que
   * espera por ela (`PermissaoService`, guards) não decidiria.
   *
   * Se já houver workspace (perfil salvo de dentro dele), recarrega com a operadora dele, para não
   * trocar a lista do workspace pela só do papel.
   */
  carregarFuncionalidadesDoPerfil$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.getIdssProfileSuccess, UserProfileActions.salvarDadosComplementaresSuccess),
      concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceOperadoraId)),
      map(([, workspaceId]) => UserProfileActions.getFuncionalidades({ regans: workspaceId ?? undefined })),
    ),
  );

  kcProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.getKeycloakProfile),
      switchMap(() =>
        from(this.kc.loadUserProfile()).pipe(
          map(kc => UserProfileActions.getKeycloakProfileSuccess({ kc }))
        ),
      ),
    ),
  );

  salvarIdssProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.salvarDadosComplementares),
      switchMap(({ idss }) => {
        const isNew = !idss.cd_usuario;
        let obs: Observable<UsuarioPerfil>;
        if (isNew) {
          obs = this.geralGoApi.criarPerfilUsuario(idss);
        } else {
          obs = this.geralGoApi.atualizarPerfilUsuario(idss);
        }
        return obs.pipe(
          tap(() => this.notificationService.success('Dados ' + (isNew ? 'salvos' : 'atualizados') + ' com sucesso')),
          map(savedProfile => UserProfileActions.salvarDadosComplementaresSuccess({ idss: savedProfile, isNew })),
          catchError((error: HttpErrorResponse) => {
            let message = `[${error.status}] ${error.message}`;
            if (JSON.stringify(error).includes('perfil cadastrado')) {
              message = 'Perfil com este email já cadastrado. Por favor entre em contato conosco.';
            }
            console.error('[Profile]', error);
            this.notificationService.error('Erro ao salvar: ' + message);
            return of(UserProfileActions.salvarDadosComplementaresFailure({ message }));
          }),
        );
      }),
    ),
  );

  aposSalvarIdssProfileRedirectToHome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.salvarDadosComplementaresSuccess),
        tap(action => {
          if (action.isNew) {
            setTimeout(() => this.router.navigate(['/']), 3000);
          }
        }),
      ),
    { dispatch: false },
  );

  completarCadastro$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      concatLatestFrom(() => [
        this.store.select(RouterSelectors.selectUrl),
        this.store.select(RouterSelectors.selectQueryParam('action')),
      ]),
      filter(([_, url, query]) => url.startsWith('/profile') && query === 'completar-cadastro'),
      map(() => UserProfileActions.completarCadastro()),
    ),
  );

  /**
   * Confere o perfil com os grupos do token (`divergenciasPerfilToken`) quando os dois estão
   * carregados, em qualquer ordem de chegada, e registra a divergência sem bloquear o usuário.
   */
  validarPerfilToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.getIdssProfileSuccess, UserProfileActions.getKeycloakProfileSuccess),
        concatLatestFrom(() => this.store.select(UserProfileSelectors.selectUserProfile)),
        filter(([, state]) => state.idss_loaded && state.kc_loaded),
        tap(([, state]) => {
          const divergencias = divergenciasPerfilToken(state.idss, this.auth.getUserPermissoes());
          if (!divergencias.length) {
            return;
          }

          console.warn('[UserProfile] perfil IDSS divergente do token do Keycloak', divergencias);
          this.geralService.registrarErro({
            ds_url_aplicacao: location.hostname,
            ds_url_api: 'validacao-perfil-token',
            ds_erro: `Perfil divergente do token - User: [${state.kc.email}] | ${divergencias.join(' | ')}`,
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private router: Router,
    private store: Store,
    private geralGoApi: GeralGoApi,
    private kc: Keycloak,
    private auth: AuthService,
    private actions$: Actions,
    private notificationService: NotificationService,
    private geralService: GeralService,
  ) {}
}
